/**
 * The bundle import in the interface (decision 7; part 24c log, table Y): Import bundle… beside Import… with its own file
 * picker, the import's status under the toolbar as the other imports' are, and the review dialog: what the bundle holds,
 * whether its book is already here (then it comes in as a copy), the book's title, Import and Cancel. What the status says
 * is announced once per phase (Y2, as part 18's A13).
 *
 * Part 24d (table Y′): a book already here also offers Update this book…; its status runs on the same action (Review the
 * update when it is ready), and the update's review says what Apply does, with each offer a check box, unticked.
 */
import type { BundleCounts } from '@cockatrice/export/bundle';
import { COVER_COLOURS } from '@cockatrice/layout';
import type { Messages } from '../i18n/en.ts';
import type { BundleImportFailure, BundleImportState, BundleReview, UpdateNote } from '../bundle-import/controller.ts';
import type { ChapterOutcome, PlannedChapter, UpdateOffer, UpdateReview } from '../bundle-import/update.ts';
import { LOCKED_ENDING } from '../backup/lock.ts';
import { focusOn } from './announce.ts';
import type { MenuItem } from '../ui/Menu.ts';
import { el, when } from '../ui/dom.ts';
import { ModalActions, ModalBody, ModalFoot } from './modal.ts';
import type { Child, On } from '../ui/dom.ts';
import { pickBundle } from './locked-import.ts';
import Button from '../ui/Button.ts';
import Checkbox from '../ui/Checkbox.ts';
import { component } from '../ui/solid.ts';
import TextField from '../ui/TextField.ts';
import type { Shell } from './shell.ts';

/** Why a bundle import gave no book, in words. */
export function bundleFailureText(shell: Pick<Shell, 't'>, reason: BundleImportFailure): string {
  const { t } = shell;
  switch (reason) {
    case 'not-bundle':
    case 'not-zip':
      return t('bundleImport.notBundle');
    case 'newer':
      return t('bundleImport.newer');
    case 'damaged':
    case 'bad-name':
      return t('bundleImport.damaged');
    case 'file-changed':
      return t('bundleImport.fileChanged');
    case 'too-large':
      return t('bundleImport.tooLarge');
    case 'unsupported':
      return t('import.unsupported');
    case 'too-many-files':
    case 'too-big':
      return t('import.tooBig');
    case 'unreadable':
      return t('import.unreadable');
    case 'timed-out':
      return t('import.timedOut');
    case 'busy':
      return t('import.busy');
    case 'exists':
      return t('bundleImport.exists');
    case 'taken':
      return t('bundleImport.taken');
    case 'not-editable':
      return t('import.notEditable');
    case 'stale':
      return t('import.stale');
    case 'write-failed':
      return t('import.writeFailed');
    case 'read-failed':
      return t('bundleImport.readFailed');
    case 'gone':
      return t('bundleImport.gone');
    // A Word or Markdown file's problems: the bundle's worker never reads one as such.
    case 'not-docx':
    case 'empty':
    case 'expands':
    case 'own-export':
    case 'failed':
      return t('bundleImport.failed');
  }
}

/** S′6: what a ready update says when it is ready again. */
const NOTES = {
  changed: 'bundleImport.updateChanged',
  'save-failed': 'bundleImport.updateSaveFailed',
  'not-editable': 'bundleImport.updateNotEditable',
} as const satisfies Record<UpdateNote, keyof Messages>;

/** A share of a whole, in whole percent. */
function percent(done: number, total: number): number {
  return total === 0 ? 0 : Math.floor((100 * done) / total);
}

/** What the status says; nothing while idle or under review. */
export function bundleImportText(shell: Pick<Shell, 't'>, state: BundleImportState): string {
  const { t } = shell;
  switch (state.kind) {
    case 'idle':
      return '';
    case 'review':
      return t('bundleImport.ready', { name: state.review.fileName });
    case 'checking':
      return t('bundleImport.checking', { name: state.fileName, percent: percent(state.done, state.total) });
    case 'storing':
      return t(state.update ? 'bundleImport.updating' : 'bundleImport.storing', { percent: percent(state.done, state.total) });
    case 'finishing':
      return t('bundleImport.finishing');
    case 'planning':
      return t('bundleImport.planning');
    case 'ready':
      return state.note === null ? t('bundleImport.updateReady', { title: state.review.title }) : t(NOTES[state.note]);
    case 'applying':
      return t('bundleImport.applying');
    case 'updated':
      return [
        t('bundleImport.updated', { title: state.title, count: state.changed }),
        ...(state.copies > 0 ? [t('bundleImport.updatedCopies', { count: state.copies })] : []),
        ...(state.added > 0 ? [t('bundleImport.updatedAdded', { count: state.added })] : []),
        ...(state.missing > 0 ? [t('bundleImport.missing', { count: state.missing })] : []),
        ...(state.recovered > 0 ? [t('bundleImport.recovered', { count: state.recovered })] : []),
      ].join(' ');
    case 'undoing':
      return t('bundleImport.undoing');
    case 'cancelled':
      return t('bundleImport.cancelled');
    case 'done':
      return [
        t(state.copy ? 'bundleImport.doneCopy' : 'bundleImport.done', { title: state.title, count: state.chapters }),
        ...(state.missing > 0 ? [t('bundleImport.missing', { count: state.missing })] : []),
        ...(state.recovered > 0 ? [t('bundleImport.recovered', { count: state.recovered })] : []),
      ].join(' ');
    case 'failed':
      return bundleFailureText(shell, state.reason);
  }
}

/** Y2: what the import announces: its phases, never each percent. */
export function bundleImportNews(shell: Pick<Shell, 't'>, state: BundleImportState): string {
  if (state.kind === 'checking') return shell.t('bundleImport.checkingNews');
  if (state.kind === 'storing') return shell.t(state.update ? 'bundleImport.updatingNews' : 'bundleImport.storingNews');
  return bundleImportText(shell, state);
}

/** Whether any file is in the sandbox or being stored: one import of any kind at a time. */
export function importing(shell: Pick<Shell, 'importer' | 'reimporter' | 'restorer'>): boolean {
  return shell.importer.busy() || shell.reimporter.busy() || shell.restorer.busy();
}

/** AC′2, AC′7: the dialog showing `review`, asked for. */
function openReview(shell: Pick<Shell, 'ui'>, review: BundleReview): void {
  shell.ui.setDialog({ kind: 'bundle-import', review });
}

/** Part 24d, S′4: the update's review, asked for. */
function openUpdate(shell: Pick<Shell, 'ui'>, review: UpdateReview): void {
  shell.ui.setDialog({ kind: 'bundle-update', review });
}

/**
 * Y1: Import bundle… as a menu's item (the library's Menu), with its own file picker (hidden, beside the menu; the item
 * opens it), off while nothing can change or one runs.
 */
export function BundleImportItem(shell: Shell, look: Pick<MenuItem, 'label' | 'hint'>): { readonly item: MenuItem; readonly picker: Child } {
  const { workspace } = shell;
  let picker: HTMLInputElement | undefined;
  return {
    item: {
      class: 'import-bundle',
      label: look.label,
      hint: look.hint,
      get disabled() {
        return !workspace.writable() || importing(shell);
      },
      onSelect: () => picker?.click(),
    },
    picker: el('input', {
      type: 'file',
      class: 'bundle-picker',
      // Part 41b, E11: a locked backup too.
      accept: `.zip,application/zip,${LOCKED_ENDING}`,
      hidden: true,
      tabIndex: -1,
      'aria-hidden': 'true',
      ref: (input: HTMLInputElement) => {
        picker = input;
      },
      onChange: (event: On<Event, HTMLInputElement>) => {
        const file = event.currentTarget.files?.[0];
        // The same file chosen again must still fire a change.
        event.currentTarget.value = '';
        if (file === undefined) return;
        // E11, E14: a locked backup asks for its password first.
        void pickBundle(shell, file);
      },
    }),
  };
}

/** Whether the status shows: while checking, storing or undoing, a review not open (AC′2, S′4), and after, with the outcome. */
function showing(shell: Pick<Shell, 'ui'>, state: BundleImportState): boolean {
  const open = shell.ui.dialog()?.kind;
  return state.kind !== 'idle' && !(state.kind === 'review' && open === 'bundle-import') && !(state.kind === 'ready' && open === 'bundle-update');
}

/** Y2: the status region; Cancel and Dismiss leave the focus on Import bundle… (A24). */
export function BundleImportStatus(shell: Shell): Child {
  const { t, restorer } = shell;
  const state = (): BundleImportState => restorer.state();
  const progress = (): { readonly done: number; readonly total: number } | null => {
    const now = state();
    return now.kind === 'checking' || now.kind === 'storing' ? now : null;
  };
  return when(
    () => showing(shell, state()),
    () =>
      el(
        'section',
        { class: 'export-status bundle-import-status', 'aria-label': t('bundleImport.label') },
        el('p', {}, () => bundleImportText(shell, state())),
        () => {
          const now = progress();
          return now === null ? null : el('progress', { max: Math.max(1, now.total), value: now.done });
        },
        // Table AC′ (deep-fix r4): one action, the same element through every phase, its role changed in place.
        el(
          'button',
          {
            type: 'button',
            class: 'bundle-import-action',
            'aria-disabled': () => (unavailable(state()) ? 'true' : null),
            onClick: () => {
              act(shell, state());
            },
          },
          () => actionText(shell, state()),
        ),
        when(
          // AC′6, AC′7: Dismiss beside an action that isn't it; S′7: a ready update's Dismiss abandons it.
          () => {
            const now = state();
            return now.kind === 'done' || now.kind === 'review' || now.kind === 'ready' || now.kind === 'updated' || (now.kind === 'failed' && now.retry !== null);
          },
          () =>
            el(
              'button',
              {
                type: 'button',
                class: 'dismiss-bundle-import',
                onClick: () => {
                  if (state().kind === 'ready') {
                    // G2: the undo and its end show on the action, which takes the focus as this button goes.
                    void restorer.discard();
                    focusOn(shell, '.bundle-import-action');
                    return;
                  }
                  restorer.dismiss();
                  focusOn(shell, '.import-bundle');
                },
              },
              t('export.dismiss'),
            ),
        ),
      ),
  );
}

/** AC′5: the phases in which the action, Cancel, does nothing: the last write (an import's or an update's), and the undo. */
function unavailable(state: BundleImportState): boolean {
  return state.kind === 'finishing' || state.kind === 'undoing' || state.kind === 'applying';
}

/** Table AC′: the action's words in `state`: Cancel in every phase that runs (AC′1, AC′4, AC′5). */
function actionText(shell: Pick<Shell, 't'>, state: BundleImportState): string {
  const { t } = shell;
  if (state.kind === 'review') return t('bundleImport.review');
  if (state.kind === 'ready') return t('bundleImport.reviewUpdate');
  if (state.kind === 'done' || state.kind === 'updated') return t('bundleImport.open');
  if (state.kind === 'failed') return state.retry === null ? t('export.dismiss') : t('bundleImport.copy');
  if (state.kind === 'cancelled') return t('export.dismiss');
  return t('bundleImport.cancel');
}

/** Table AC′: what pressing the action in `state` does. */
function act(shell: Shell, state: BundleImportState): void {
  const { restorer } = shell;
  if (state.kind === 'review') openReview(shell, state.review);
  else if (state.kind === 'ready') openUpdate(shell, state.review);
  // S3c.
  else if (state.kind === 'done' || state.kind === 'updated') {
    // Part 11d, R8: Open the book shows it in the writer.
    void shell.workspace.openBook(state.bookId).then(() => {
      shell.go('write');
      shell.focusEditor();
    });
  }
  else if (state.kind === 'failed' && state.retry !== null) {
    restorer.reviewCopy();
    openReview(shell, state.retry);
  } else if (state.kind === 'failed' || state.kind === 'cancelled') {
    // AC′8, G2, G3.
    restorer.dismiss();
    focusOn(shell, '.import-bundle');
  } else if (!unavailable(state)) {
    // AC′1, AC′9: Cancel while checking ends it, the status gone, the focus back on Import bundle…; AC′4: while storing,
    // the undo shows on this same action, which keeps the focus.
    const checking = state.kind === 'checking';
    restorer.cancel();
    if (checking) focusOn(shell, '.import-bundle');
  }
}

/** What the bundle holds beside its chapters, each count above none, in the review's order. */
const HELD = [
  ['trash', 'bundleImport.trash'],
  ['copies', 'bundleImport.copies'],
  ['snapshots', 'bundleImport.snapshots'],
  ['kept', 'bundleImport.pictures'],
  ['notes', 'bundleImport.notes'],
  ['comments', 'bundleImport.comments'],
] as const satisfies readonly (readonly [keyof BundleCounts, keyof Messages])[];

/** Table L: what the review says becomes of the book: a new book (L1), or a copy and why (L2–L4). */
const WHERE = {
  new: 'bundleImport.newBook',
  here: 'bundleImport.here',
  meanwhile: 'bundleImport.meanwhile',
  clash: 'bundleImport.clash',
} as const satisfies Record<NonNullable<BundleReview['copy']> | 'new', keyof Messages>;

/** Y3: the review dialog's body. */
export function BundleImportReview(shell: Shell, review: BundleReview): Child {
  const { t, workspace, restorer, ui } = shell;
  const { counts } = review.check;
  let title = review.title;
  // Found pictures come back through the pipeline, so they count as pictures.
  const held = HELD.flatMap(([key, message]) => {
    const count = key === 'kept' ? counts.kept + counts.found : counts[key];
    return count > 0 ? [t(message, { count })] : [];
  });
  return [
    ModalBody(
      { class: 'bundle-review' },
      el('p', {}, t('bundleImport.summary', { count: counts.chapters })),
      held.length > 0 ? [el('p', {}, t('bundleImport.also')), el('ul', { class: 'bundle-holds' }, held.map((line) => el('li', {}, line)))] : null,
      counts.missing > 0 ? el('p', {}, t('bundleImport.noPictures', { count: counts.missing })) : null,
      counts.refused > 0 ? el('p', {}, t('bundleImport.damagedRecords', { count: counts.refused })) : null,
      el('p', { class: 'bundle-where' }, t(WHERE[review.copy ?? 'new'])),
      component(TextField, {
        id: 'bundle-import-title',
        label: t('bundleImport.bookTitle'),
        defaultValue: review.title,
        onChange: (value: string) => {
          title = value;
        },
      }),
    ),
    ModalFoot(
      {},
      ModalActions(
        component(Button, {
          onClick: () => {
            restorer.closeReview();
            ui.setDialog(null);
            // G4 (deep-fix r5).
            focusOn(shell, '.import-bundle');
          },
          children: t('bundleImport.cancel'),
        }),
        // Y′1: a book already here can be updated from the bundle instead (L11's update).
        review.copy === 'here'
          ? component(Button, {
              class: 'update-bundle-import',
              get disabled() {
                return !workspace.writable();
              },
              onClick: () => {
                void restorer.confirmUpdate();
                ui.setDialog(null);
                focusOn(shell, '.bundle-import-action');
              },
              children: t('bundleImport.update'),
            })
          : null,
        component(Button, {
          variant: 'primary',
          class: 'confirm-bundle-import',
          get disabled() {
            return !workspace.writable();
          },
          onClick: () => {
            // Confirmed before the dialog closes: its close puts away only a review still waiting.
            void restorer.confirm(title);
            ui.setDialog(null);
            // AC′4: on the action, which keeps the focus through the store's end without anything moving it.
            focusOn(shell, '.bundle-import-action');
          },
          children: t(review.copy === null ? 'bundleImport.confirm' : 'bundleImport.confirmCopy'),
        }),
      ),
    ),
  ];
}

/** Y′3: each group of the update's review, by the outcome its chapters share, in the review's order. */
const GROUPS = [
  ['taken', 'bundleImport.updTaken'],
  ['merged', 'bundleImport.updMerged'],
  ['copied', 'bundleImport.updCopied'],
] as const satisfies readonly (readonly [ChapterOutcome, keyof Messages])[];

/** Y′3: the outcomes the review counts without naming each chapter. */
const COUNTED = [
  ['newer-here', 'bundleImport.updNewer'],
  ['unchanged', 'bundleImport.updSame'],
  ['in-history', 'bundleImport.updInHistory'],
  ['no-text', 'bundleImport.updNoText'],
  ['unreadable', 'bundleImport.updUnreadable'],
  ['gone', 'bundleImport.updGone'],
] as const satisfies readonly (readonly [ChapterOutcome, keyof Messages])[];

/** F2, F4, F5: an offer's words. */
function offerText(shell: Pick<Shell, 't'>, offer: UpdateOffer): string {
  const { t } = shell;
  switch (offer.kind) {
    case 'title':
      return t('bundleImport.offerTitle', { chapter: offer.chapter, value: offer.value });
    case 'synopsis':
      return t('bundleImport.offerSynopsis', { chapter: offer.chapter });
    case 'subtitle':
      return t('bundleImport.offerSubtitle', { chapter: offer.chapter, value: offer.value });
    case 'note': {
      // Part 19e: a chapter's note by its chapter; a Notebook note by its title, or as untitled.
      const { before, after } = offer.note;
      if (offer.chapter !== null) return t('bundleImport.offerNotes', { chapter: offer.chapter });
      if (before.title === after.title) return before.title === '' ? t('bundleImport.offerNoteUntitled') : t('bundleImport.offerNote', { name: before.title });
      return t('bundleImport.offerNoteRenamed', { name: before.title, renamed: after.title });
    }
    case 'detail': {
      if (offer.unit === 'cover') return t('bundleImport.offerCover');
      // Part 11h: a cover colour by its name.
      if (offer.unit === 'coverColour') {
        const colour = COVER_COLOURS.find((each) => each === offer.value);
        return t('bundleImport.offerCoverColour', { colour: colour === undefined ? offer.value : t(`cover.colour.${colour}`) });
      }
      // F′4–F′6: each detail named as an offer says it (the epigraph's source goes with its quote).
      const field = t(`bundleImport.field.${offer.unit}`);
      return offer.free ? t('bundleImport.offerDetailFree', { field }) : t('bundleImport.offerDetail', { field, value: offer.value });
    }
    case 'theme':
      return t('bundleImport.offerTheme');
  }
}

/** F′2, F′3, F′5, F′6 (deep-fix r2): the bundle's free text a box takes, shown under it; null for a short value, named in its label. */
function offerShown(offer: UpdateOffer): string | null {
  if (offer.kind === 'synopsis' || offer.kind === 'note') return offer.value;
  return offer.kind === 'detail' && offer.free ? offer.value : null;
}

/** A chapter's line in its group: its title, and for a merge the passages both changed. */
function chapterLine(shell: Pick<Shell, 't'>, chapter: PlannedChapter): string {
  return chapter.conflicts > 0 ? shell.t('bundleImport.updConflicts', { title: chapter.title, count: chapter.conflicts }) : chapter.title;
}

/** Part 24d, Y′3: the update's review: what Apply does to each chapter, what it adds, each offer unticked; Apply and Cancel. */
export function BundleUpdateReview(shell: Shell, review: UpdateReview): Child {
  const { t, workspace, restorer, ui } = shell;
  const ticked = new Set<string>();
  const { counts } = review;
  const of = (outcome: ChapterOutcome): readonly PlannedChapter[] => review.chapters.filter((chapter) => chapter.outcome === outcome);
  // What its history and notes bring that the book lacks (N5–N10).
  const also = (
    [
      ['copies', 'bundleImport.copies'],
      ['snapshots', 'bundleImport.snapshots'],
      ['pictures', 'bundleImport.pictures'],
      ['notes', 'bundleImport.notes'],
      ['comments', 'bundleImport.comments'],
    ] as const
  ).flatMap(([key, message]) => (counts[key] > 0 ? [t(message, { count: counts[key] })] : []));
  const list = (lines: readonly string[]): Child => el('ul', { class: 'bundle-holds' }, lines.map((line) => el('li', {}, line)));
  const keptTexts = review.chapters.filter((chapter) => chapter.kept !== null).length;
  return [
    ModalBody(
      { class: 'bundle-update-review' },
      el('p', {}, t('bundleImport.updateIntro')),
      GROUPS.map(([outcome, message]) => {
        const chapters = of(outcome);
        return chapters.length === 0 ? null : el('section', { class: 'bundle-update-group' }, el('p', {}, t(message, { count: chapters.length })), list(chapters.map((chapter) => chapterLine(shell, chapter))));
      }),
      review.added.length > 0 ? el('section', { class: 'bundle-update-group' }, el('p', {}, t('bundleImport.updAdded', { count: review.added.length })), list(review.added.map((chapter) => chapter.title))) : null,
      COUNTED.map(([outcome, message]) => {
        const count = of(outcome).length;
        return count === 0 ? null : el('p', {}, t(message, { count }));
      }),
      review.onlyHere > 0 ? el('p', {}, t('bundleImport.updOnlyHere', { count: review.onlyHere })) : null,
      // B′ (deep-fix r1): where the bundle's words go that Apply doesn't take whole.
      keptTexts > 0 ? el('p', {}, t('bundleImport.updKept', { count: keptTexts })) : null,
      also.length > 0 ? [el('p', {}, t('bundleImport.updAlso')), list(also)] : null,
      counts.missing > 0 ? el('p', {}, t('bundleImport.noPictures', { count: counts.missing })) : null,
      counts.recovered > 0 ? el('p', {}, t('bundleImport.damagedRecords', { count: counts.recovered })) : null,
      review.offers.length > 0
        ? el(
            'fieldset',
            { class: 'bundle-update-offers' },
            el('legend', {}, t('bundleImport.updOffers')),
            review.offers.map((offer, at) => {
              // Deep-fix r2: the box shows the bundle's words, so the tick is an informed choice.
              const shown = offerShown(offer);
              const described = `bundle-update-theirs-${String(at)}`;
              return [
                component(Checkbox, {
                  class: 'bundle-update-offer',
                  label: offerText(shell, offer),
                  ...(shown === null ? {} : { 'aria-describedby': described }),
                  onChange: (checked: boolean) => {
                    if (checked) ticked.add(offer.key);
                    else ticked.delete(offer.key);
                  },
                }),
                shown === null ? null : el('blockquote', { class: 'bundle-update-theirs', id: described }, shown),
              ];
            }),
          )
        : null,
    ),
    ModalFoot(
      {},
      ModalActions(
        component(Button, {
          onClick: () => {
            // S′7: put away, the staging abandoned; G2: its undo and end on the status's action.
            void restorer.discard();
            ui.setDialog(null);
            focusOn(shell, '.bundle-import-action');
          },
          children: t('bundleImport.cancel'),
        }),
        component(Button, {
          variant: 'primary',
          class: 'apply-bundle-update',
          get disabled() {
            return !workspace.writable();
          },
          onClick: () => {
            // Applying before the dialog closes: its close discards only an update still waiting.
            void restorer.apply(new Set(ticked));
            ui.setDialog(null);
            focusOn(shell, '.bundle-import-action');
          },
          children: t('bundleImport.apply'),
        }),
      ),
    ),
  ];
}
